import { Grid, TextField, Tooltip, Box, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import React from "react";
import { Stack } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import ClassifierCheatsheet from "components/Classifiers/ClassifierCheatsheet";
import {
    ClassifierBuilderV2Store,
    ClassifierBuilderView,
} from "../../Stores/ClassifierBuilderV2Store";
import { useStore } from "utils/useStore";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { useNavigate } from "react-router";
import StandardClassifierBuilder from "./StandardView/StandardClassifierBuilder";
import AcxToast from "components/UI/AcxToast";
import InfoIcon from "@mui/icons-material/Info";
import { ClassifierBuilderV2SwitchViewDialog } from "../Dialogs/ClassifierBuilderV2SwitchViewDialog";
import { basicDateReferenceOptions } from "../../../../../stores/ComponentStores/DatePickerComponentStore";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import { sxClsx } from "utils/styleClsx";

const ClassifierBuilderRuleset = observer(() => {
    const theme = useTheme();
    const store = useStore(ClassifierBuilderV2Store);
    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const navigate = useNavigate();

    const validateAndTestRuleSet = async () => {
        try {
            store.setTestClassifierError("");
            store.setResultsLoading(true);
            store.setValidationLoading(true);

            await store.validateRbcFtsSearchString();

            runInAction(async () => {
                if (store.isRbcFtsStringValid && !store.testClassifierError)
                    await store.testClassifier();

                if (!store.testClassifierError) {
                    navigate("../results");
                }
                store.setValidationLoading(false);
                store.getHighlights();
            });
        } catch (error) {
            throw error;
        } finally {
            store.setResultsLoading(false);
        }
    };

    React.useEffect(() => {
        store.setTestClassifierError("");
        store.setPageNumberOnly(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const standardBuilderTooltip = (
        <>
            <Stack direction="row" alignItems="center">
                <InfoIcon
                    sx={{
                        height: "16px",
                        width: "16px",
                        marginLeft: theme.spacing(0.5),
                    }}
                />
                <Box fontWeight="bold">Building a Classifier</Box>
            </Stack>
            <ol style={{ paddingLeft: theme.spacing(1) }}>
                <li>
                    Select <b>'+ New Group'</b> to begin building your
                    classifier. All rules within a group will be combined (think
                    of it as combining rules within parentheses).
                </li>
                <li>
                    Select <b>'+ Rule'</b> to add a rule to determine what
                    results should be returned.
                </li>
                <li>
                    A rule selector will appear with a list of a available{" "}
                    <b>operators</b>.
                </li>
                <li>
                    Once an operator is selected, define the <b>value</b>.
                </li>
                <li>
                    Continue to add rules using logical operators (AND/OR) to
                    determine how rules within a group will be combined.
                </li>
                <li>
                    Select <b>'Test Rule Set'</b> to generate results that match
                    your classifier.
                </li>
            </ol>
        </>
    );

    const onSwitchView = () => {
        store.rbcSearchTermStr
            ? store.toggleSwitchViewDialog()
            : store.toggleView();
    };

    return (
        <>
            <Grid item>
                <TitledSection
                    title={
                        <Grid container justifyContent="space-between">
                            <Grid item>
                                {store.view ===
                                ClassifierBuilderView.Standard ? (
                                    <>
                                        Classifier Builder
                                        <Tooltip
                                            placement="right-start"
                                            title={standardBuilderTooltip}
                                        >
                                            <InfoIcon
                                                sx={{
                                                    height: "16px",
                                                    width: "16px",
                                                    marginLeft:
                                                        theme.spacing(0.5),
                                                }}
                                                color="primary"
                                            />
                                        </Tooltip>
                                    </>
                                ) : (
                                    "Advanced"
                                )}
                            </Grid>
                            <Grid item>
                                <AcxButton
                                    size="small"
                                    variant="outlined"
                                    onClick={onSwitchView}
                                    sx={{ fontWeight: "bold" }}
                                >
                                    {store.getNextView()}
                                </AcxButton>
                            </Grid>
                        </Grid>
                    }
                >
                    <Grid container>
                        {store.showTestRuleSetWarning && (
                            <Grid
                                item
                                container
                                style={{
                                    position: "relative",
                                    marginBottom: "1rem",
                                }}
                                id={"toast--test-rule-set-error"}
                            >
                                <span style={{ width: "100%" }}>
                                    <AcxToast
                                        fullWidth
                                        severity="error"
                                        message={store.testClassifierError}
                                        title={""}
                                        onClose={() =>
                                            store.setTestClassifierError("")
                                        }
                                        fullWidthMessage={true}
                                    />
                                </span>
                            </Grid>
                        )}

                        {store.view === ClassifierBuilderView.Standard ? (
                            <StandardClassifierBuilder
                                store={store.standardCBStore}
                            />
                        ) : (
                            <Grid
                                container
                                item
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    height:"100%",
                                }}
                            >
                                <Grid lg={8} xs={12} item>
                                    <TextField
                                        sx={sxClsx({
                                            height: "fit-content",
                                            width: "100%",
                                        })}
                                        id="search-term-entry"
                                        label="Enter Search Terms"
                                        multiline
                                        maxRows={17}
                                        minRows={17}
                                        value={store.rbcSearchTermStr || ""}
                                        variant="outlined"
                                        inputProps={{
                                            style: {
                                                fontFamily:
                                                    "SFMono-Regular, Menlo,Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                                            },
                                        }}
                                        InputLabelProps={{
                                            style: {
                                                fontFamily:
                                                    "SFMono-Regular, Menlo,Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                                            },
                                        }}
                                        onChange={(event) =>
                                            store.setRbcSearchTermStr(
                                                event.target.value,
                                            )
                                        }
                                        error={!!store.testClassifierError}
                                        required
                                    />
                                </Grid>
                                <Grid
                                    item
                                    lg={4}
                                    xs={12}
                                    sx={{ height: "100%" }}
                                >
                                    <ClassifierCheatsheet />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </TitledSection>
            </Grid>

            <Grid
                item
                container
                sx={{
                    marginTop: theme.spacing(4),
                    "& > *": {
                        marginRight: theme.spacing(4),
                    },
                }}
                alignItems="flex-end"
            >
                <AcxDateRangeInput
                    onSelect={(start, end) =>
                        applicationFiltersStore.quickFiltersStore.setDatePair(
                            start,
                            end,
                        )
                    }
                    defaultStartDate={
                        applicationFiltersStore.quickFiltersStore.startDate
                    }
                    defaultEndDate={
                        applicationFiltersStore.quickFiltersStore.endDate
                    }
                    datePickerStore={
                        applicationFiltersStore.quickFiltersStore
                            .datePickerStore
                    }
                    displayDateType
                    dateReferenceOptions={basicDateReferenceOptions}
                    maxWidth="100%"
                    inputStyle={{ width: "100%" }}
                    wrapperStyle={{ maxWidth: "100%" }}
                />
                <AcxButton
                    color="primary"
                    onClick={validateAndTestRuleSet}
                    sx={{ marginLeft: theme.spacing(1), marginTop: "5px" }}
                    loading={store.validationLoading}
                    disabled={store.standardCBStore.hasError}
                >
                    Test Rule Set
                </AcxButton>
            </Grid>
            <ClassifierBuilderV2SwitchViewDialog store={store} />
        </>
    );
});

export default ClassifierBuilderRuleset;
